import React, { InputHTMLAttributes, LegacyRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { getRadioVariant } from './radio.utils';
import { RadioIndicator } from './radioIndicator.component';
import { RadioText } from './radioText.component';

const radioContainerStyles = tv({
  base: 'inline-flex items-center p-s16 rounded-2xl border-2 cursor-pointer transition-colors border-transparent bg-surface-subtle hover:border-border-action-active hover:bg-surface-action-selected',
  variants: {
    size: {
      sm: 'px-s16 py-s8',
      md: 'px-s16 py-s16',
    },
    defaultBackgroundColor: {
      subtle: 'bg-surface-subtle',
      primary: 'bg-surface-primary border-2 border-surface-subtle',
    },
    isFullWidth: {
      true: 'w-full',
    },
    variant: {
      active: 'border-border-action-active bg-surface-action-selected',
      disabled:
        'cursor-not-allowed bg-surface-disabled hover:bg-surface-disabled hover:border-transparent',
    },
    defaultVariants: {
      size: 'md',
      defaultBackgroundColor: 'subtle',
    },
  },
});

type RadioProps = {
  title: string;
  description?: string;
  defaultBackgroundColor?: 'subtle' | 'primary';
  id: string;
  checked?: boolean;
  classnames?: string;
  disabled?: boolean;
  name: string;
  variantSize?: 'sm' | 'md';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
  isFullWidth?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const Radio = forwardRef(
  (
    {
      title,
      description,
      id,
      checked,
      disabled,
      classnames,
      name,
      onChange,
      isFullWidth,
      defaultBackgroundColor,
      variantSize = 'md',
      isLoading = false,
      ...rest
    }: RadioProps,
    ref?: LegacyRef<HTMLInputElement>,
  ) => {
    const variant = getRadioVariant(disabled, checked);
    return (
      <div className={twMerge(classnames, isFullWidth && 'w-full')}>
        <label
          htmlFor={id}
          className={twMerge(
            radioContainerStyles({
              variant,
              isFullWidth,
              size: variantSize,
              defaultBackgroundColor,
            }),
          )}>
          <input
            ref={ref}
            type="radio"
            id={id}
            name={name}
            value={title}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="hidden peer"
            {...rest}
          />
          <RadioIndicator isLoading={isLoading} checked={checked} disabled={disabled} />
          <RadioText title={title} description={description} disabled={disabled} />
        </label>
      </div>
    );
  },
);

Radio.displayName = 'Radio';
